// The time clock, shared by the server and the screens.

import { dayKey, formatTime, zone } from './format.js';

/**
 * An open shift older than this stops counting: nobody works 16 hours straight, so someone forgot
 * to clock out. Hours shows it as "Didn’t clock out — fix it" until a manager puts in the time.
 */
export const STALE_SHIFT_MS = 16 * 3_600_000;

/** Whether an open shift (no clock-out) has gone on too long to count. */
export function isStaleShift(clockInAt: number, clockOutAt: number | null, now = Date.now()): boolean {
  return clockOutAt === null && now - clockInAt > STALE_SHIFT_MS;
}

/** The day before a YYYY-MM-DD day (plain calendar math, so time changes don't matter). */
function dayBefore(key: string, days = 1): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - days)).toISOString().slice(0, 10);
}

/**
 * When a shift started, with the day when it isn't today: "10:02 AM", "yesterday 10:02 AM",
 * "Mon 10:02 AM" (this past week) or "Oct 9 10:02 AM", in the store's time zone.
 */
export function sinceWords(ms: number, now = Date.now(), timeZone = zone.current): string {
  const time = formatTime(ms, timeZone);
  const day = dayKey(ms, timeZone);
  const today = dayKey(now, timeZone);
  if (day === today) return time;
  if (day === dayBefore(today)) return `yesterday ${time}`;
  for (let i = 2; i < 7; i++) {
    if (day === dayBefore(today, i)) return `${new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(new Date(ms))} ${time}`;
  }
  return `${new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric' }).format(new Date(ms))} ${time}`;
}

function zoneParts(ms: number, timeZone: string): Record<string, string> {
  return Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      .formatToParts(new Date(ms)).filter((x) => x.type !== 'literal').map((x) => [x.type, x.value]),
  );
}

/** A time as the box on the screen shows it: "2026-10-12T18:30" in the store's time zone. */
export function wallClock(ms: number, timeZone = zone.current): string {
  const p = zoneParts(ms, timeZone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/**
 * "2026-10-12T18:30" read as store time → epoch ms, or null when it isn't a real time. The box
 * on the screen sends store time, so a computer set to another time zone still gets it right.
 */
export function fromWallClock(text: string, timeZone = zone.current): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(text ?? '').trim());
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number);
  const want = Date.UTC(y, mo - 1, d, h, mi);
  const check = new Date(want);
  if (check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d || h > 23 || mi > 59) return null;
  let t = want;
  for (let i = 0; i < 2; i++) {
    const p = zoneParts(t, timeZone);
    t -= Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute)) - want;
  }
  return t;
}
